/**
 * One track inside a playlist, with its position, reorder controls and remove.
 *
 * REORDER IS TWO BUTTONS, not drag-and-drop. §12 asks for reordering "where
 * practical", and a pointer-drag implementation is not: it needs a keyboard
 * alternative to be usable at all, it needs a touch implementation that does not fight
 * the page scroll, and it needs live-region announcements to be followable without
 * sight. Up/Down buttons are all three for free, they work identically on a phone, and
 * they are what a track list of this size actually needs. Drag can be added on top
 * later; it cannot be a replacement for these.
 *
 * THE TITLE IS NO LONGER A LINK INTO THE STUDIO. It was — an <a> to /library/:songId
 * labelled "Open <title> in Lyrixa Studio" — so tapping a track's name inside a playlist
 * left the playlist and landed in an editing workspace. Playing is what a playlist row is
 * for. The title now raises the lyrics preview through the surface's `onSelect`, matching
 * the library row, and the Studio is an explicit item in the row's More menu.
 *
 * It is still a real <button> for the title rather than a link, since there is no longer a
 * URL behind the gesture; the reorder and remove controls stop propagation for the usual
 * reason.
 */
import {
  ArrowDown,
  ArrowUp,
  Music,
  Pause,
  PictureInPicture2,
  Play,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import type { ActionMenuItem } from '../ui';
import { ActionMenu, IconButton } from '../ui';
import { PlayingIndicator } from '../player';
import { SongArtwork } from '../songs';
import { joinWithMiddleDot } from '../../lib/formatting';
import { c } from '../../styles/theme';
import type { Song } from '../../types/domain';

export interface PlaylistTrackRowProps {
  song: Song;
  /** 1-based position, shown in the row. */
  position: number;
  /** True when this song is the loaded one AND advancing. */
  playing: boolean;
  /**
   * True when this song is the loaded one, running or paused. Defaults to `playing`.
   *
   * What the row's highlight keys off, so pausing track 3 of 8 leaves track 3 looking
   * like the one you are on. The Play/Pause control keys off `playing`.
   */
  active?: boolean;
  /**
   * What pressing the title does. Replaces the `href` that pointed at the Studio.
   *
   * Whatever selection means on the surface drawing the row — the playlist screen raises
   * the lyrics preview, as the Library does.
   */
  onSelect: (song: Song) => void;
  /**
   * Opens this track in the Studio, from the row's More menu.
   *
   * Optional, and absent means no such item rather than a disabled one — the same rule
   * SongCard's optional menu items follow.
   */
  onOpenInStudio?: (song: Song) => void;
  /**
   * Starts the playlist from this track — or pauses/resumes when it is already loaded.
   *
   * Both gestures on one control, because that is what a play button is: pressing it on
   * the track you are hearing must not restart the playlist from that track. See
   * Playlist.tsx, which decides which of the two the press means.
   */
  onPlay: () => void;
  /**
   * Plays the playlist from this track IN the mini player — starts it and raises the bar.
   *
   * The explicit request for the floating transport, in the row's More menu. `onPlay` above
   * deliberately does not raise it: playing from this screen keeps the playlist's own
   * transport and queue panel as the surface, and a bar arriving over them was asked for by
   * nobody. Someone who wants playback to follow them off this page says so here.
   *
   * NOT A SEPARATE PLAYLIST PLAYER. It starts the same queue through the same store, so the
   * bar's Prev/Next step the play order this screen set — see Playlist.tsx and usePlayer's
   * `playQueueInMiniPlayer`.
   *
   * Optional, so a surface that passes no handler renders exactly as before.
   */
  onPlayInMiniPlayer?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onRemove: () => void;
  /** Name of the playlist, for the remove button's accessible name. */
  playlistName: string;
}

export default function PlaylistTrackRow({
  song,
  position,
  playing,
  active = playing,
  onSelect,
  onOpenInStudio,
  onPlay,
  onPlayInMiniPlayer,
  onMoveUp,
  onMoveDown,
  onRemove,
  playlistName,
}: PlaylistTrackRowProps) {
  /*
    The More menu's items, built before the JSX because both are optional and the menu is
    rendered only if at least one survives. Studio first, as on the library row.

    "Play in Mini Player" names the playlist as well as the song, because that is what the
    press does — it starts the play order from here, not the one track — and because a screen
    reader hears this out of the row's context, where "Play Summer highway in the mini player"
    would be indistinguishable from the library row's item for the same song.
  */
  const menuItems: ActionMenuItem[] = [];
  if (onOpenInStudio) {
    menuItems.push({
      label: 'Open in Studio',
      // The name the title's link used to carry, moved onto the deliberate gesture.
      ariaLabel: `Open ${song.title} in Lyrixsa Studio`,
      icon: <SlidersHorizontal size={13} color={c.text2} />,
      onSelect: () => onOpenInStudio(song),
    });
  }
  if (onPlayInMiniPlayer) {
    menuItems.push({
      label: 'Play in Mini Player',
      ariaLabel: `Play ${playlistName} from ${song.title} in the mini player`,
      icon: <PictureInPicture2 size={13} color={c.text2} />,
      onSelect: onPlayInMiniPlayer,
    });
  }

  return (
    <div
      className="lyx-track-row lyx-playlist-track"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        // Active, not playing: the row you paused on is still the row you are on, and
        // dropping the highlight on pause would make a pause read as a stop.
        border: `1px solid ${active ? c.accentBorder : c.border}`,
        borderRadius: 10,
        background: c.surface1,
        padding: '8px 10px',
      }}
    >
      {/* Monospace so the numbers line up down the list — the same reason times and
          prices use it everywhere else in the app. */}
      <span
        className="lyx-playlist-pos"
        style={{ fontFamily: 'IBM Plex Mono', fontSize: 11, color: c.text3, flexShrink: 0 }}
        aria-hidden="true"
      >
        {position}
      </span>

      <SongArtwork
        song={song}
        height={36}
        radius={7}
        style={{ width: 36, flexShrink: 0, overflow: 'hidden' }}
      >
        <Music
          size={11}
          color="rgba(255,255,255,0.5)"
          style={{ position: 'absolute', bottom: 4, left: 5 }}
        />
      </SongArtwork>

      {/* The title, as a button rather than the Studio link it was. "View", because that
          is what the press now does — see the header. `textAlign: left` because a button
          centres its contents and this is two lines of prose. */}
      <button
        type="button"
        onClick={() => onSelect(song)}
        aria-label={`View ${song.title}`}
        style={{
          flex: 1,
          minWidth: 0,
          background: 'none',
          border: 'none',
          padding: 0,
          textAlign: 'left',
          cursor: 'pointer',
          color: 'inherit',
          font: 'inherit',
        }}
      >
        <span
          style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 500,
            color: c.text1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {song.title}
        </span>
        <span
          style={{
            display: 'block',
            fontSize: 11,
            color: c.text2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {joinWithMiddleDot([song.genre, song.length])}
        </span>
      </button>

      <div
        className="lyx-row-controls"
        style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}
      >
        {/*
          The name changes with the state, which it did not before: this control was
          permanently "Play …", so the track you were listening to offered to play itself
          and a screen reader was told the playlist was stopped while it was running.

          "Pause" without the playlist, because pausing is not an act on the playlist —
          the name only carries "from X" while the press would start one.
        */}
        {/*
          The app's one playing visual — the music bar's waveform, four bars, from this
          song's own seeded shape. See player/PlayingIndicator, which is where the reason it
          sits BESIDE this control rather than inside it is written down (Batch 6 §13).
        */}
        {playing && <PlayingIndicator seed={song.id} playing />}

        <IconButton
          label={playing ? `Pause ${song.title}` : `Play ${song.title} from ${playlistName}`}
          onClick={onPlay}
          style={{ width: 30, height: 30, borderRadius: 8 }}
        >
          {/* A normal play/pause control: the glyph is which way the press goes, and the
              waveform beside it is whether anything is playing. */}
          {playing ? (
            <Pause size={13} color={c.accent} />
          ) : (
            <Play size={13} color={active ? c.accent : c.text2} />
          )}
        </IconButton>

        {/*
          Rendered disabled at the ends rather than omitted, so the controls do not
          move as you shuffle a track up the list — the button you just pressed stays
          under your finger until it can no longer do anything.
        */}
        <IconButton
          label={`Move ${song.title} up`}
          disabled={!onMoveUp}
          onClick={onMoveUp}
          style={{ width: 30, height: 30, borderRadius: 8 }}
        >
          <ArrowUp size={13} color={c.text2} />
        </IconButton>
        <IconButton
          label={`Move ${song.title} down`}
          disabled={!onMoveDown}
          onClick={onMoveDown}
          style={{ width: 30, height: 30, borderRadius: 8 }}
        >
          <ArrowDown size={13} color={c.text2} />
        </IconButton>

        {/* "Remove from X", never "Delete": the song stays in the library, and the
            two words mean different things on a screen that has both. */}
        <IconButton
          label={`Remove ${song.title} from ${playlistName}`}
          onClick={onRemove}
          style={{ width: 30, height: 30, borderRadius: 8 }}
        >
          <X size={13} color={c.text2} />
        </IconButton>

        {/*
          THE ROW'S SECONDARY ACTIONS — the Studio, and the mini player.

          A menu rather than more glyphs: this row already carries four controls, and on a
          phone they wrap onto their own full-width line (see `.lyx-playlist-track` in
          global.css) — a fifth and sixth unlabelled icon there is how a track list becomes a
          control panel. Both items send this track somewhere other than where it is, which is
          what the menu is for.

          RENDERED ONLY WHEN THERE IS SOMETHING IN IT, which is why the gate is on the array
          rather than on `onOpenInStudio` as it used to be: with two optional handlers, gating
          on one of them would hide a wired mini-player item on any surface that does not
          offer the Studio. Absent handlers mean absent items rather than disabled ones — the
          same rule SongCard's optional items follow.
        */}
        {menuItems.length > 0 && (
          <ActionMenu
            label={`More actions for ${song.title}`}
            triggerStyle={{ width: 30, height: 30 }}
            items={menuItems}
          />
        )}
      </div>
    </div>
  );
}
